import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { datum, zeitpunkt } from '../format'
import { laufPfad } from '../pfade'
import { IMPORTSTATUS } from '../texte'
import type { ExcelArt, ExcelBlatt, ExcelDoppelt, ExcelImport, Importstatus } from '../typen'

/**
 * Die Karte „Entscheidungen" über dem Excel-Plan (18.09.2026). Vorher standen je Blatt bis zu sieben
 * Warnungen untereinander, und die Nutzerin musste selbst herausfinden, welche davon eine Handlung
 * verlangt. Jetzt leitet die Seite die Punkte aus dem Plan ab und fragt sie einzeln - eine Frage, zwei
 * bis drei Knöpfe, „noch 3 offen". Jede Antwort schreibt in den Entwurf der Seite; die Seite liest neu,
 * wo die Antwort das Lesen ändert (Art, Gruppe, Kopplung), und merkt den Punkt erst, wenn das Lesen
 * geglückt ist. Was das Gedächtnis schon entschieden hat (`blatt.entscheidungen`), wird nicht erneut
 * gefragt - bei Kopplungen aber nur für genau den Block, der in der Entscheidung steht.
 */

export type Entwurf = Record<string, { art: ExcelArt; gruppe: string; aktiv: boolean; kopplungen: Record<string, string> }>

/** Ein Punkt kennt die Blätter, die seine Antwort ändert - bei gebündelten Fragen sind es mehrere. */
export type Entscheidungspunkt = { schluessel: string; blaetter: ExcelBlatt[] } & (
  | { typ: 'doppelt'; laeufe: ExcelDoppelt[] }
  | { typ: 'blattart'; blatt: ExcelBlatt; art: ExcelArt; grund: 'unsicher' | 'ki' }
  | { typ: 'stand'; lauf_id: number; datenstand: string; eigenerStand: string | null }
  | { typ: 'abgewaehlt'; blatt: ExcelBlatt; grund: string | null }
  | { typ: 'gruppe'; blatt: ExcelBlatt; neu: string; vorhanden: string[] }
  | { typ: 'kopplung'; blatt: ExcelBlatt; block: string; nummern: string }
  | { typ: 'artikel_neu'; blatt: ExcelBlatt; artikel: string[] }
)

/** Was die Nutzerin auf einen Punkt geantwortet hat - die Seite setzt den Entwurf danach. */
export type Antwort =
  | { typ: 'weiter' }
  | { typ: 'art'; art: ExcelArt }
  | { typ: 'aktiv' }
  | { typ: 'gruppe'; gruppe: string }
  | { typ: 'kopplung'; nummern: string }
  | { typ: 'verwerfen' }

/** Der Name einer Art aus ihrer Beschreibung: „PLAN - IST: Forecast GF je …" wird „PLAN - IST". */
export function artName(beschreibung: string): string {
  const doppelpunkt = beschreibung.indexOf(':')
  return doppelpunkt > 0 ? beschreibung.slice(0, doppelpunkt) : beschreibung
}

/** Ein Blockname in einer Zeile - die Excel trägt Zeilenumbrüche in den Zellen. */
export function blockName(block: string): string {
  return block.replace(/\s+/g, ' ').slice(0, 60)
}

/** Ein gemerkter Wert in Worten - für Tooltip und Tabelle; Kopplungen sind Listen oder Blockzuordnungen. */
export function wertText(wert: unknown): string {
  if (wert === null || wert === undefined) return ''
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein'
  if (typeof wert === 'string' || typeof wert === 'number') return String(wert)
  if (Array.isArray(wert)) return wert.map(wertText).join(', ')
  if (typeof wert === 'object') {
    const o = wert as Record<string, unknown>
    if (Array.isArray(o.nummern)) return `${o.block ? `${String(o.block)}: ` : ''}${o.nummern.map(wertText).join(', ')}`
    return Object.entries(o)
      .map(([k, v]) => `${k}: ${wertText(v)}`)
      .join('; ')
  }
  return String(wert)
}

/**
 * Steht für dieses Blatt eine Entscheidung der Art im Gedächtnis? Eine Kopplung gilt nur für ihren Block
 * (`e.block`); ohne Block in der Entscheidung oder in der Frage schaltet sie keine Frage stumm - lieber
 * einmal zu viel gefragt als eine Kopplung still übernommen.
 */
function gemerkt(blatt: ExcelBlatt, art: 'blattart' | 'gruppe' | 'aktiv' | 'kopplung', block?: string): boolean {
  return (blatt.entscheidungen ?? []).some((e) => {
    if (e.art !== art) return false
    if (art !== 'kopplung') return true
    return block !== undefined && e.block === block
  })
}

/** Warum ist das Blatt abgewählt? Der Server nennt den Grund als Feld (`abwahl_grund`); ein Plan von vor
 *  diesem Feld hat keinen - dann steht die Frage ohne Begründung. */
export function abwahlGrund(blatt: ExcelBlatt): string | null {
  return blatt.abwahl_grund ?? null
}

/** Das Häkchen eines Blatts nach dem Entwurf - der Entwurf gewinnt, sonst gilt, was der Server vorschlägt. */
export function blattAktiv(blatt: ExcelBlatt, entwurf: Entwurf): boolean {
  const e = entwurf[blatt.blatt]
  return (e?.aktiv ?? blatt.aktiv) && (e?.art ?? blatt.art) !== 'ignorieren'
}

/**
 * Die Entscheidungspunkte eines Plans in der Reihenfolge, in der sie gefragt werden: erst die Datei
 * (schon geladen?), dann was überhaupt geladen wird (älterer Stand - gebündelt je Lauf und Datum -,
 * abgewählte Blätter), dann je Blatt Art, Gruppe, Kopplungen, neue Artikel. Blätter, die nicht geladen
 * werden, bekommen nur die Frage nach dem Laden - alles andere wäre müßig.
 */
export function entscheidungspunkte(daten: ExcelImport, entwurf: Entwurf): Entscheidungspunkt[] {
  const punkte: Entscheidungspunkt[] = []
  if ((daten.plan.doppelt?.length ?? 0) > 0) punkte.push({ schluessel: 'doppelt', blaetter: [], typ: 'doppelt', laeufe: daten.plan.doppelt ?? [] })
  const kiGeaendert = new Set(daten.ki?.geaendert ?? daten.plan.ki?.geaendert ?? [])

  // Gleiche Fragen bündeln: alle Blätter, die wegen desselben Laufs und desselben Datums abgewählt sind,
  // bekommen einen Punkt - sonst steht dieselbe Frage vier Mal hintereinander.
  const buendel = new Map<string, { lauf_id: number; datenstand: string; eigenerStand: string | null; blaetter: ExcelBlatt[] }>()
  for (const b of daten.plan.blaetter) {
    const aktiv = entwurf[b.blatt]?.aktiv ?? b.aktiv
    if (aktiv || !b.aelterer_stand || gemerkt(b, 'aktiv')) continue
    const schluessel = `${b.aelterer_stand.lauf_id}|${b.aelterer_stand.datenstand}|${b.datenstand ?? ''}`
    const vorhanden = buendel.get(schluessel)
    if (vorhanden) vorhanden.blaetter.push(b)
    else buendel.set(schluessel, { lauf_id: b.aelterer_stand.lauf_id, datenstand: b.aelterer_stand.datenstand, eigenerStand: b.datenstand ?? null, blaetter: [b] })
  }
  for (const [schluessel, b] of buendel) {
    punkte.push({ schluessel: `stand:${schluessel}`, blaetter: b.blaetter, typ: 'stand', lauf_id: b.lauf_id, datenstand: b.datenstand, eigenerStand: b.eigenerStand })
  }

  for (const b of daten.plan.blaetter) {
    const e = entwurf[b.blatt]
    const art = e?.art ?? b.art
    const aktiv = e?.aktiv ?? b.aktiv
    // Abgewählt ohne älteren Stand: die Heuristik hält das Blatt für stillgelegt oder leer. Das ist eine
    // eigene Frage - „ignorieren" ist keine, denn dort hat die Nutzerin die Art in der Hand.
    if (!aktiv && !b.aelterer_stand && art !== 'ignorieren' && !gemerkt(b, 'aktiv')) {
      punkte.push({ schluessel: `abgewaehlt:${b.blatt}`, blaetter: [b], typ: 'abgewaehlt', blatt: b, grund: abwahlGrund(b) })
    }
    if (!aktiv || art === 'ignorieren') continue
    if ((b.sicherheit === 'unsicher' || kiGeaendert.has(b.blatt)) && !gemerkt(b, 'blattart')) {
      punkte.push({ schluessel: `art:${b.blatt}`, blaetter: [b], typ: 'blattart', blatt: b, art, grund: b.sicherheit === 'unsicher' ? 'unsicher' : 'ki' })
    }
    // Die neue Gruppe kommt als Feld vom Server (`gruppe_neu`, `gruppen_vorhanden`); aus einem Satz ließe
    // sie sich nicht verlässlich lesen - Gruppennamen dürfen Kommas tragen.
    if (b.gruppe_neu && (b.gruppen_vorhanden?.length ?? 0) > 0 && !gemerkt(b, 'gruppe')) {
      punkte.push({ schluessel: `gruppe:${b.blatt}`, blaetter: [b], typ: 'gruppe', blatt: b, neu: b.gruppe_neu, vorhanden: b.gruppen_vorhanden ?? [] })
    }
    for (const block of b.offene_vorschlaege ?? []) {
      if (gemerkt(b, 'kopplung', block)) continue
      const nummern = e?.kopplungen[block] ?? (b.kopplungen ?? b.kladde?.kopplungen_vorschlag ?? {})[block]?.join(', ') ?? ''
      punkte.push({ schluessel: `kopplung:${b.blatt}:${block}`, blaetter: [b], typ: 'kopplung', blatt: b, block, nummern })
    }
    if ((b.artikel_neu?.length ?? 0) > 0) punkte.push({ schluessel: `neu:${b.blatt}`, blaetter: [b], typ: 'artikel_neu', blatt: b, artikel: b.artikel_neu ?? [] })
  }
  return punkte
}

export default function Entscheidungskarte({
  punkte,
  beantwortet,
  gesamt,
  arten,
  gesperrt,
  keinBlattAktiv,
  onAntwort,
}: {
  punkte: Entscheidungspunkt[]
  beantwortet: Set<string>
  /** alle je gestellten Punkte - beantwortete bleiben gezählt, damit die Zahl nicht springt */
  gesamt: number
  arten: Record<ExcelArt, string>
  gesperrt: boolean
  keinBlattAktiv: boolean
  onAntwort: (punkt: Entscheidungspunkt, antwort: Antwort) => void
}) {
  const offen = punkte.filter((p) => !beantwortet.has(p.schluessel))
  const aktuell = offen[0]
  return (
    <div className="karte entscheidungen" aria-live="polite">
      <div className="leiste">
        <b>Entscheidungen</b>
        {offen.length > 0 && <span className="entscheidung-fortschritt">noch {offen.length} offen</span>}
        {offen.length === 0 && gesamt > 0 && !keinBlattAktiv && <span className="status gebucht">alles entschieden</span>}
        {offen.length === 0 && gesamt === 0 && !keinBlattAktiv && <span className="status gebucht">nichts zu entscheiden</span>}
        {offen.length === 0 && keinBlattAktiv && <span className="status gesperrt">kein Blatt wird geladen</span>}
        {gesamt > 0 && (
          <span className="hinweis">
            {gesamt - offen.length} von {gesamt} beantwortet
          </span>
        )}
      </div>
      {offen.length === 0 && keinBlattAktiv && <p className="hinweis">Kein Blatt wird geladen - so schreibt der Import nichts.</p>}
      {offen.length === 0 && !keinBlattAktiv && gesamt === 0 && <p className="hinweis">Der Plan wirft keine Frage auf - die Datei lässt sich so importieren.</p>}
      {offen.length === 0 && !keinBlattAktiv && gesamt > 0 && (
        <p className="hinweis">
          {gesamt === 1 ? 'Der eine Punkt ist' : `Alle ${gesamt} Punkte sind`} beantwortet. „So importieren“ übernimmt die Vorgaben und schreibt.
        </p>
      )}
      {aktuell && (
        <Frage
          key={aktuell.schluessel}
          punkt={aktuell}
          arten={arten}
          gesperrt={gesperrt}
          fokussieren={beantwortet.size > 0}
          onAntwort={(a) => onAntwort(aktuell, a)}
        />
      )}
    </div>
  )
}

/** Eine Frage mit ihren Knöpfen. Ein zweiter Schritt („Ändern", „Andere Art", „Vorhandene wählen") klappt ein Feld auf. */
function Frage({
  punkt,
  arten,
  gesperrt,
  fokussieren,
  onAntwort,
}: {
  punkt: Entscheidungspunkt
  arten: Record<ExcelArt, string>
  gesperrt: boolean
  /** nach einer Antwort den Fokus auf die neue Frage setzen - beim ersten Öffnen der Seite nicht */
  fokussieren: boolean
  onAntwort: (antwort: Antwort) => void
}) {
  const [zweiterSchritt, setZweiterSchritt] = useState(false)
  const [feld, setFeld] = useState(punkt.typ === 'kopplung' ? punkt.nummern : '')
  const frageRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (fokussieren) frageRef.current?.focus()
    // nur beim Wechsel der Frage - `key` baut die Komponente je Punkt neu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (punkt.typ === 'doppelt') {
    return (
      <>
        <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
          Diese Datei wurde schon geladen - trotzdem noch einmal importieren?
        </p>
        <p className="hinweis">
          {punkt.laeufe.map((d, i) => (
            <span key={d.lauf_id}>
              {i > 0 ? '; ' : ''}
              <Link className="hinweis-link" to={laufPfad({ id: d.lauf_id, dokumenttyp: 'excel' })}>
                Lauf {d.lauf_id}
              </Link>{' '}
              ({IMPORTSTATUS[d.status as Importstatus]?.text ?? d.status}, {zeitpunkt(d.entschieden_am ?? d.angelegt_am)})
            </span>
          ))}
          . Ein zweiter Import ersetzt, was der erste gebucht hat, legt nichts doppelt an.
        </p>
        <div className="leiste">
          <button className="haupt" disabled={gesperrt} onClick={() => onAntwort({ typ: 'weiter' })}>
            Trotzdem
          </button>
          <button disabled={gesperrt} onClick={() => onAntwort({ typ: 'verwerfen' })}>
            Lauf verwerfen
          </button>
        </div>
      </>
    )
  }
  if (punkt.typ === 'blattart') {
    return (
      <>
        <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
          Blatt „{punkt.blatt.blatt}“ als „{artName(arten[punkt.art])}“ lesen?
        </p>
        <p className="hinweis">
          {punkt.grund === 'unsicher' ? 'Die KI ist sich bei der Zuordnung nicht sicher.' : 'Die KI hat die Art gegenüber der ersten Erkennung geändert.'} {arten[punkt.art]}.
        </p>
        <div className="leiste">
          <button className="haupt" disabled={gesperrt} onClick={() => onAntwort({ typ: 'weiter' })}>
            Ja
          </button>
          {!zweiterSchritt && (
            <button disabled={gesperrt} onClick={() => setZweiterSchritt(true)}>
              Andere Art
            </button>
          )}
          {zweiterSchritt && (
            <>
              {/* Die Auswahl ändert nur das Feld; erst der Knopf antwortet - sonst löst ein Streifen der
                  Tastatur durch die Liste ein Neu-Lesen nach dem anderen aus. */}
              <select autoFocus disabled={gesperrt} value={feld} onChange={(e) => setFeld(e.target.value)}>
                <option value="">Art wählen…</option>
                {(Object.keys(arten) as ExcelArt[])
                  .filter((a) => a !== punkt.art)
                  .map((a) => (
                    <option key={a} value={a} title={arten[a]}>
                      {artName(arten[a])}
                    </option>
                  ))}
              </select>
              <button className="haupt" disabled={gesperrt || !feld} onClick={() => onAntwort({ typ: 'art', art: feld as ExcelArt })}>
                Diese Art übernehmen
              </button>
            </>
          )}
        </div>
      </>
    )
  }
  if (punkt.typ === 'stand') {
    const mehrere = punkt.blaetter.length > 1
    const eigener = punkt.eigenerStand ? `vom ${datum(punkt.eigenerStand)}` : 'ohne Datum im Blatt'
    return (
      <>
        <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
          {mehrere
            ? `${punkt.blaetter.length} Blätter sind ${eigener}, geladen ist der Stand vom ${datum(punkt.datenstand)} (Lauf ${punkt.lauf_id}).`
            : punkt.eigenerStand
              ? `Blatt „${punkt.blaetter[0].blatt}“ ist vom ${datum(punkt.eigenerStand)}, geladen ist der Stand vom ${datum(punkt.datenstand)} (Lauf ${punkt.lauf_id}).`
              : `Blatt „${punkt.blaetter[0].blatt}“ hat kein Datum im Blatt, geladen ist der Stand vom ${datum(punkt.datenstand)} (Lauf ${punkt.lauf_id}).`}
        </p>
        <p className="hinweis">
          Beim Laden würde der ältere Stand den neueren ersetzen - deshalb {mehrere ? 'sind die Blätter' : 'ist das Blatt'} abgewählt.
          {mehrere ? ` Betroffen: ${punkt.blaetter.map((b) => b.blatt).join(', ')}.` : ''}
        </p>
        <div className="leiste">
          <button className="haupt" disabled={gesperrt} onClick={() => onAntwort({ typ: 'weiter' })}>
            {mehrere ? 'Alle abgewählt lassen' : 'Abgewählt lassen'}
          </button>
          <button disabled={gesperrt} onClick={() => onAntwort({ typ: 'aktiv' })}>
            {mehrere ? 'Alle laden' : 'Trotzdem laden'}
          </button>
        </div>
      </>
    )
  }
  if (punkt.typ === 'abgewaehlt') {
    return (
      <>
        <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
          Blatt „{punkt.blatt.blatt}“ ist abgewählt{punkt.grund ? ` (${punkt.grund})` : ''} - laden?
        </p>
        <p className="hinweis">Abgewählt heißt: Dieses Blatt schreibt nichts. Zum Laden den Haken bewusst setzen.</p>
        <div className="leiste">
          <button className="haupt" disabled={gesperrt} onClick={() => onAntwort({ typ: 'weiter' })}>
            Abgewählt lassen
          </button>
          <button disabled={gesperrt} onClick={() => onAntwort({ typ: 'aktiv' })}>
            Laden
          </button>
        </div>
      </>
    )
  }
  if (punkt.typ === 'gruppe') {
    return (
      <>
        <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
          Blatt „{punkt.blatt.blatt}“ nennt die Gruppe „{punkt.neu}“, die es noch nicht gibt - vorhanden sind {punkt.vorhanden.join(', ')}.
        </p>
        <div className="leiste">
          {!zweiterSchritt && (
            <button className="haupt" disabled={gesperrt} onClick={() => setZweiterSchritt(true)}>
              Vorhandene wählen
            </button>
          )}
          {zweiterSchritt && (
            <>
              <select autoFocus disabled={gesperrt} value={feld} onChange={(e) => setFeld(e.target.value)}>
                <option value="">Gruppe wählen…</option>
                {punkt.vorhanden.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              <button className="haupt" disabled={gesperrt || !feld} onClick={() => onAntwort({ typ: 'gruppe', gruppe: feld })}>
                Diese Gruppe übernehmen
              </button>
            </>
          )}
          <button disabled={gesperrt} onClick={() => onAntwort({ typ: 'weiter' })}>
            Als neue Gruppe anlegen
          </button>
        </div>
      </>
    )
  }
  if (punkt.typ === 'kopplung') {
    return (
      <>
        <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
          Blatt „{punkt.blatt.blatt}“, Block „{blockName(punkt.block)}“ - Kopplung {punkt.nummern || '(leer)'} übernehmen?
        </p>
        <p className="hinweis">
          Der Block hat keine Produktnummer; die Fertigartikel sind ein Vorschlag
          {punkt.blatt.kladde ? ` (von ${punkt.blatt.kladde.fertigartikel.map((f) => f.nummer).join(', ')})` : ''}.
        </p>
        <form
          className="leiste"
          onSubmit={(e) => {
            e.preventDefault()
            if (zweiterSchritt && feld.trim()) onAntwort({ typ: 'kopplung', nummern: feld })
          }}
        >
          {!zweiterSchritt && (
            <button type="button" className="haupt" disabled={gesperrt || !punkt.nummern.trim()} onClick={() => onAntwort({ typ: 'weiter' })}>
              Übernehmen
            </button>
          )}
          {!zweiterSchritt && (
            <button type="button" disabled={gesperrt} onClick={() => setZweiterSchritt(true)}>
              Ändern
            </button>
          )}
          {!zweiterSchritt && !punkt.nummern.trim() && <span className="hinweis">Ohne Nummern lässt sich der Block nicht übernehmen - „Ändern“ trägt sie ein.</span>}
          {zweiterSchritt && (
            <>
              <input type="text" autoFocus value={feld} disabled={gesperrt} style={{ width: 260 }} onChange={(e) => setFeld(e.target.value)} placeholder="Nummern, kommagetrennt" />
              <button type="submit" className="haupt" disabled={gesperrt || !feld.trim()}>
                Diese Kopplung übernehmen
              </button>
            </>
          )}
        </form>
      </>
    )
  }
  return (
    <>
      <p className="entscheidung-frage" ref={frageRef} tabIndex={-1}>
        Blatt „{punkt.blatt.blatt}“ legt {punkt.artikel.length === 1 ? 'einen neuen Artikel' : `${punkt.artikel.length} neue Artikel`} an.
      </p>
      <p className="hinweis">
        {punkt.artikel.slice(0, 20).join(', ')}
        {punkt.artikel.length > 20 ? ` und ${punkt.artikel.length - 20} weitere` : ''}. Gruppe und Kopplung bekommen sie aus dem Blatt; die Stammdaten lassen sich danach ergänzen.
      </p>
      <div className="leiste">
        <button className="haupt" disabled={gesperrt} onClick={() => onAntwort({ typ: 'weiter' })}>
          Weiter
        </button>
      </div>
    </>
  )
}
